import {
  CLIENT_TYPE,
  Condition,
  FinanceType,
  Origin,
  ProtectionPlansSelectionType,
} from './constants';
import type { Dealer } from './dealerTypes';
import type { Car } from './carTypes';
import type { Deal } from './dealTypes';
import type { OfferData } from './deskingTypes';
import type { DMSCodeType, DTEntityId, TekionEntity } from './dmsTypes';
import type { InsuranceAddress, InsuranceType } from './financingTypes';

export type ProductId = string;
export type BundleId = string;

export interface Bundle {
  id: BundleId;
  name: string;
  description: string;
  imageUrl: string;
  markups: { [index in Origin]?: number };
  discounts: { [index in Origin]?: number };
  productIds: ProductId[];
  enabled: boolean;
  selectedInStore?: ProtectionPlansSelectionType;
  selectedOnline?: ProtectionPlansSelectionType;

  // The following properties are legacy and no longer set.
  // They may be present in copies of bundles, such as those found in
  // `pencil.data.selectedBundles` or desking offers.
  // Instead of trying to read these directly, use the helpers in pen-utils.
  // https://upstartnetwork.atlassian.net/browse/REL-8229
  mandatory?: boolean;
  defaultSelectedOnline?: boolean;
  defaultSelectedInStore?: boolean;
  highlightedText?: string;
  excludedProductIds?: ProductId[];
}

export type ConditionFinanceType<T> = {
  [Condition.NEW]: {
    [FinanceType.CASH]: T;
    [FinanceType.PURCHASE]: T;
    [FinanceType.LEASE]: T;
  };
  [Condition.USED]: {
    [FinanceType.CASH]: T;
    [FinanceType.PURCHASE]: T;
  };
  [Condition.CPO]: {
    [FinanceType.CASH]: T;
    [FinanceType.PURCHASE]: T;
  };
};

export interface ConditionInputValuesForCoverage {
  [Condition.NEW]: {
    vin?: string;
  };
  [Condition.USED]: {
    vin?: string;
    mileage?: string;
  };
  [Condition.CPO]: {
    vin?: string;
    mileage?: string;
  };
}

export enum PriceAdjustmentType {
  NUMBER = 'number',
  PERCENT = 'percent',
}

export enum ProductCategory {
  ACCESSORY = 'accessory',
  FRONT_END_ADD_ON = 'frontEndAddOn',
  GAP = 'gap',
  INSURANCE = 'insurance',
  SERVICE_CONTRACT = 'serviceContract',
  SURFACE_PROTECTION_PRODUCT = 'surfaceProtectionProduct',
  THEFT_DETERRENT_DEVICE = 'theftDeterrentDevice',
}

export interface AllDeductiblesForCoverage{
  [key: string]: {
    Amount: number;
    DealerCost: number;
    DeductibleType: string;
    Description: string;
    FIMarkup: number;
    MaxRetailPrice: number;
    MinRetailPrice: number;
    RateID: number;
    ReducedAmount: number;
    RetailPrice: number;
  }
}

export interface CoverageNameAndDeductibleOption {
  displayName: string | null;
  instoreEnabled: boolean;
  minAge: number | null;
  maxAge: number | null;
  minMileage: number | null;
  maxMileage: number | null;
  allDeductiblesForCoverage?: AllDeductiblesForCoverage;
  isDefault: boolean;
  coverageName: string;
  deductible?: string | null;
}

export interface ProductCoverage {
  coverageName: string;
  allTermsForCoverage: string[];
  allDeductiblesForCoverage: AllDeductiblesForCoverage
}

export interface DefaultTermsByCondition {
  [Condition.NEW]?: string;
  [Condition.USED]?: string;
  [Condition.CPO]?: string;
}

interface BaseProduct {
  id: ProductId; // UUID or stringified raw productId
  productId?: ProductId;
  imageUrl?: string;
  videoUrl?: string;
  displayName?: string;
  priceAdjustmentType: PriceAdjustmentType;
  markups?: { [index in Origin]?: number };
  discounts?: { [index in Origin]?: number };
  enabled: boolean;
  availability: ConditionFinanceType<boolean>;
  taxed?: boolean;
  backend?: boolean;
  productCategory?: ProductCategory;
  // @todo - below is for web rewrite. some may be unnecessary
  brochureUrl?: string;
  description?: string[];
  rates?: ProductRate[];
  termMiles?: number;
  termMonths?: number;
  monthlyPayment?: number;
  selected?: boolean;
  defaultTerm?: string;
  defaultTermsByCondition?: DefaultTermsByCondition;
  selectedInStore?: ProtectionPlansSelectionType;
  highlightedText?: string;
  selectedOnline?: ProtectionPlansSelectionType;

  // also known as option code for WeOwe products
  dmsCode?: string | null;

  dtEntityId?: DTEntityId;
  tekionEntity?: TekionEntity; // stores mapped tekion entity information on the product

  // This helps if the product has to be categorized under WeOwe
  // when the deal is pushed to CDK. To be deprecated in favor of dmsCodeType
  isWeOweProduct?: boolean;
  // A more generic way of determining what the dms code represents
  dmsCodeType?: DMSCodeType;
  insuranceType?: InsuranceType;
  insuranceAddress?: InsuranceAddress;
  insuranceCompanyName?: string;

  // The following properties are legacy and no longer set.
  // They may be present in copies of bundles, such as those found in
  // `pencil.data.selectedProducts` or desking offers.
  // Instead of trying to read these directly, use the helpers in pen-utils.
  // https://upstartnetwork.atlassian.net/browse/REL-8229
  mandatory?: boolean;
  defaultSelectedOnline?: boolean;
  defaultSelectedInStore?: boolean;

  inCapCostForLease?: boolean;
  inCapCostForOnePayLease?: boolean;
}

export enum ProductSource {
  PROVIDER = 'provider',
  USER = 'user',
}

export type ProviderId = number;

export type CoverageNameAndDeductible = {
  coverageName?: string;
  deductible?: string | null;
  displayName?: string
};

// Product retrieved from PEN.
export interface ProviderProduct extends BaseProduct {
  raw: {
    name: string;
    productId: number;
    productType: string;
    providerId: ProviderId;
  };
  coverageNamesAndDeductibles: ConditionFinanceType<CoverageNameAndDeductible | null>;
  coverageNamesAndDeductiblesOptions?: ConditionFinanceType<CoverageNameAndDeductibleOption [] >;
  availableCoverageNames?: string[];
  providerDealerCode?: string;
  productDealerCode?: string; // only for US Warranty
  rateBookCode?: string;
  source: ProductSource.PROVIDER;
}

// Product created by the dealer
export interface UserProduct extends BaseProduct {
  retailPrice?: number;
  source: ProductSource.USER;
}

// Formatted PEN product
export type Product = ProviderProduct | UserProduct;

// Selected in the deal (could be many)
export interface SelectedProduct {
  productId: ProductId;
  // If a selectedProduct was selected within a bundle, we need to set the bundleId
  // If selected outside of a bundle, explicitly set it to null
  bundleId: BundleId | null;
  // Below are set by the client
  termMiles: number;
  termMonths: number;
  // Below are fetched from the backend with getRates
  amount: number;
  dealerCost: number;
  retailPriceWithoutBundleDiscountApplied: number;
  retailPrice: number;
  effectiveDiscountPercentage?: number;

  // DMS Code that gets pushed by the CRM to CDK - is optional
  dmsCode?: string | null;
  dtEntityId?: DTEntityId;
  // This helps if the product has to be categorized under WeOwe
  // when the deal is pushed to CDK. To be deprecated in favor of dmsCodeType
  isWeOweProduct?: boolean;
  // A more generic way of determining what the dms code represents
  dmsCodeType?: DMSCodeType;

  tekionEntity?: TekionEntity; // stores mapped tekion entity information on the product

  deductible?: string | null;
  coverageName?: string;
  monthlyPrice?: number;
  taxed?: boolean;
  displayName?: string;
  monthlyPayment?: number;
  maxRetailPrice?: number;
  minRetailPrice?: number;

  source?: ProductSource;

  inCapCostForLease?: boolean;
  inCapCostForOnePayLease?: boolean;
}

export type SelectedProductWithFinalPrice = SelectedProduct & {
  finalPrice: number;
};

// Alias for SelectedBundleId
export type SelectedBundleId = BundleId;

export interface Provider {
  id: ProviderId;
  name: string;
}

export interface RatePaymentOptions {
  dealer: Dealer;
  car: Car;
  deal: Deal;
  clientType: CLIENT_TYPE;
  offerData?: OfferData | null;
  carPrice?: number | null;
  discount?: number | null;
  downPayment?: number | null;
  includeAllCoverageNames?: boolean;
}

export interface PENConfiguration {
  // PEN dealer id
  dealerId: number;

  // List of PEN providers
  providers: Provider[];

  // Bundles and products nested by condition and finance type
  bundles: ConditionFinanceType<Bundle[]>;

  // Should the UI display products? bundles?
  enableInUI: {
    products: boolean;
    bundles: boolean;
  };

  // Allow Users to select more than one Bundle in a deal
  multiBundlesInDeal: boolean;

  // If set to true, don't show product prices in Online and don't sum them in UPGRADES pricing section
  hideProductPrices?: boolean;

  // If true, we will calculate a daily product price based on the monthly
  showDailyPrice?: boolean;

  // If true, we will show the full cost, along with the monthly/daily cost
  showTotalCost?: boolean;

  validProductCodes?: Record<
    number,
    { providerDealerCode: string; productDealerCode?: string }
  >;
}

export type BundlePrices = {
  [key: string]: {
    retailPriceWithoutBundleDiscountApplied: number;
    retailPrice: number;
    monthlyPayment: number;
    totalEffectiveDiscountPercentage?: number;
  };
};

export type ProductRates = {
  [key: string]: ProductRate[];
};

export type ProductRate = {
  term?: number;
  miles?: number;
  deductible?: {
    amount: number;
    retailPrice: number;
    monthlyPayment: number;
    effectiveDiscountPercentage?: number | null;
    minRetailPrice: number;
    maxRetailPrice: number;
    deductibleFullName: string | null;
  };
  bundlePrices?: BundlePrices;
  // @todo - below is for web rewrite. should come back and clean this up
  effectiveDiscountPercentage?: number | null;
  monthlyPayment?: number;
  termMiles?: number;
  termMonths?: number;
  deductibleFullName?: string;
  isDefault?: boolean;
  coverageName?: string;
  isDefaultCoverageName?: boolean;
  coverageDisplayName?: string;
};

// @todo - below is for web rewrite. should come back and clean this up
export type ProductTerm = {
  label?: string;
  value: {
    termMiles: number;
    termMonths: number;
  };
  deductible: {
    amount: number;
    retailPrice: number;
    monthlyPayment: number;
    effectiveDiscountPercentage?: number | null;
    minRetailPrice: number;
    maxRetailPrice: number;
    deductibleFullName: string | null;
  };
  bundlePrices: BundlePrices;
};

export type BundlePrice = {
  price: number;
  retail?: number;
  monthly?: number;
  discount: number;
};

export enum ProtectionPackageSelectionPages {
  BUNDLES = 'BUNDLES',
  PRODUCTS = 'PRODUCTS',
}
